
import React, { useState, useEffect } from 'react';
import { Profile, Company, UserRole } from '@/lib/types';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { UserRoleSelect } from './UserRoleSelect';
import { UserCompanySelect } from './UserCompanySelect';
import { DeleteUserButton } from './DeleteUserButton';
import { ResetPasswordDialog } from './ResetPasswordDialog';
import { UserSubscriptionDialog } from './UserSubscriptionDialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Key, CreditCard } from 'lucide-react';
import { getTierDisplayName } from '@/services/subscriptions/access';
import { SubscriptionTier } from '@/services/subscriptions/types';
import { supabase } from '@/integrations/supabase/client';

interface UsersTableProps {
  users: Profile[];
  companies: Company[];
  isLoading: boolean;
  deletingUserId: string | null;
  onUserUpdated: (user: Profile) => void;
  onUserDeleted: (userId: string) => void;
  setDeletingUserId: (userId: string | null) => void;
  onResetPasswordOpen?: (userEmail: string) => void;
  onResetPasswordSuccess?: (userEmail: string) => void;
}

export const UsersTable: React.FC<UsersTableProps> = ({
  users,
  companies,
  isLoading,
  deletingUserId,
  onUserUpdated,
  onUserDeleted,
  setDeletingUserId,
  onResetPasswordOpen,
  onResetPasswordSuccess
}) => {
  const [resetPasswordUserId, setResetPasswordUserId] = useState<string | null>(null);
  const [resetPasswordUserName, setResetPasswordUserName] = useState<string>('');
  const [resetPasswordUserEmail, setResetPasswordUserEmail] = useState<string>('');
  const [subscriptionDialogOpen, setSubscriptionDialogOpen] = useState(false);
  const [selectedSubscriptionUser, setSelectedSubscriptionUser] = useState<{
    id: string;
    name: string;
    tier: SubscriptionTier;
  } | null>(null);
  const [userSubscriptions, setUserSubscriptions] = useState<Record<string, { tier: SubscriptionTier }>>({});

  useEffect(() => {
    fetchUserSubscriptions();
  }, [users]);

  const fetchUserSubscriptions = async () => {
    if (users.length === 0) return;
    
    const { data } = await supabase
      .from('subscriptions')
      .select('user_id, tier')
      .in('user_id', users.map(u => u.id));

    if (data) {
      const subscriptionsMap: Record<string, { tier: SubscriptionTier }> = {};
      data.forEach(sub => {
        subscriptionsMap[sub.user_id] = { tier: sub.tier as SubscriptionTier };
      });
      setUserSubscriptions(subscriptionsMap);
    }
  };

  const handleRoleChange = (userId: string, newRole: UserRole) => {
    const updatedUser = users.find(u => u.id === userId);
    if (updatedUser) {
      onUserUpdated({
        ...updatedUser,
        role: newRole
      });
    }
  };

  const handleCompanyChange = (userId: string, companyId: string | null) => {
    const updatedUser = users.find(u => u.id === userId);
    if (updatedUser) {
      onUserUpdated({
        ...updatedUser,
        company_id: companyId
      });
    }
  };

  const handleResetPassword = (userId: string, userName: string, userEmail: string) => {
    setResetPasswordUserId(userId);
    setResetPasswordUserName(userName);
    setResetPasswordUserEmail(userEmail);
    onResetPasswordOpen?.(userEmail);
  };

  const closeResetPasswordDialog = () => {
    setResetPasswordUserId(null);
    setResetPasswordUserName('');
    setResetPasswordUserEmail('');
  };

  const handleManageSubscription = (user: Profile) => {
    const tier = userSubscriptions[user.id]?.tier || 'free';
    setSelectedSubscriptionUser({
      id: user.id,
      name: user.first_name && user.last_name 
        ? `${user.first_name} ${user.last_name}`
        : user.email || 'Unknown User',
      tier
    });
    setSubscriptionDialogOpen(true);
  };

  const getTierBadgeColor = (tier: SubscriptionTier) => {
    const colors = {
      free: "bg-gray-500",
      starter: "bg-blue-500",
      pro: "bg-purple-500",
      unlimited: "bg-gradient-to-r from-yellow-500 to-orange-500"
    };
    return colors[tier] || colors.free;
  };

  if (isLoading) {
    return <div className="flex justify-center p-10">Loading users...</div>;
  }

  if (users.length === 0) {
    return (
      <div className="text-center py-8">
        No users found matching your search criteria.
      </div>
    );
  }

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>User</TableHead>
            <TableHead>Email</TableHead>
            <TableHead>Role</TableHead>
            <TableHead>Company</TableHead>
            <TableHead>Subscription</TableHead>
            <TableHead>Created</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {users.map((user) => {
            const userName = user.first_name && user.last_name 
              ? `${user.first_name} ${user.last_name}`
              : user.email || 'Unknown User';
            
            return (
              <TableRow key={user.id}>
                <TableCell>
                  <div className="flex items-center gap-3">
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={user.avatar_url || ''} />
                      <AvatarFallback>
                        {user.first_name && user.last_name
                          ? `${user.first_name[0]}${user.last_name[0]}`.toUpperCase()
                          : 'U'}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      {userName}
                    </div>
                  </div>
                </TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>
                  <UserRoleSelect 
                    userId={user.id} 
                    currentRole={user.role as UserRole}
                    onRoleChange={handleRoleChange}
                    userName={userName}
                  />
                </TableCell>
                <TableCell>
                  <UserCompanySelect 
                    userId={user.id}
                    currentCompanyId={user.company_id}
                    currentRole={user.role as UserRole}
                    companies={companies}
                    onCompanyChange={handleCompanyChange}
                  />
                </TableCell>
                <TableCell>
                  <Badge className={getTierBadgeColor(userSubscriptions[user.id]?.tier || 'free')}>
                    {getTierDisplayName(userSubscriptions[user.id]?.tier || 'free')}
                  </Badge>
                </TableCell>
                <TableCell>
                  {new Date(user.created_at).toLocaleDateString()}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleManageSubscription(user)}
                      title="Manage subscription"
                    >
                      <CreditCard className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleResetPassword(user.id, userName, user.email || '')}
                      title="Reset password"
                    >
                      <Key className="h-4 w-4" />
                    </Button>
                    <DeleteUserButton 
                      userId={user.id}
                      userName={userName}
                      isDeleting={deletingUserId === user.id}
                      onDeleteStart={setDeletingUserId}
                      onDeleteComplete={onUserDeleted}
                    />
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      {resetPasswordUserId && (
        <ResetPasswordDialog
          isOpen={true}
          onClose={closeResetPasswordDialog}
          userId={resetPasswordUserId}
          userName={resetPasswordUserName}
          userEmail={resetPasswordUserEmail}
          onSuccess={(email) => onResetPasswordSuccess?.(email)}
        />
      )}

      {selectedSubscriptionUser && (
        <UserSubscriptionDialog
          open={subscriptionDialogOpen}
          onOpenChange={setSubscriptionDialogOpen}
          userId={selectedSubscriptionUser.id}
          userName={selectedSubscriptionUser.name}
          currentTier={selectedSubscriptionUser.tier}
          onSuccess={() => {
            onUserUpdated(users.find(u => u.id === selectedSubscriptionUser.id)!);
            fetchUserSubscriptions();
          }}
        />
      )}
    </>
  );
};
